import type { SampleStage, SampleStatus } from '@ctcells/contract'

import type { SampleGroupShape } from '../recipes/recipeForm.ts'

import type { ChamberProgress } from './chamberProgress.ts'

import {
  sortFlowRows,
  type FlowCell,
  type FlowCellState,
  type FlowRow,
  type StageVerdict,
} from './sampleFlowModel.ts'
import type { StageTimeLookup, StageTimes } from './stageTimes.ts'

/**
 * 환자별 챔버 그림 — **시료 탭의 유일한 그림.**
 *
 * eric 지시(2026-09-21 오후): 「환자별 그림 자체가 레이아웃이 깨져 있고 … 시료가 5개인데
 * **그림과 시료 아이디가 중복**돼. **그림 위주로** 해당 진행상황을 다시 그려줘. 기존의 시료
 * 흐름과 챔버별 그림 중 **시료 흐름 부분을 없애고 환자별 챔버 그림으로 합쳐**줘.」
 *
 * ⇒ 종전에는 위에 **시료 × 단계 격자**(`SampleFlow.tsx`)가 있고 아래에 이 그림이 있었다.
 *   같은 시료 ID · 같은 단계 진행이 **두 번** 나왔다. 격자를 걷고 그것이 말하던 것
 *   (「이 시료가 5단계 중 어디까지 왔나」)을 **카드 «안»으로** 옮겼다. 그림이 주인이다.
 *
 * 🔴 **트레이 격자를 흉내 내지 않는다.** 3×3 자리 배치를 그리면 「그 칸에 있다」는 말이 되는데
 *    우리는 시료의 물리 자리를 모른다(BR-31 · `Sample.current_position` 0건). 여기 그리는 것은
 *    **묶음의 구성**이지 배치도가 아니다.
 *
 * 🔴 **상태를 «새로» 만들지 않는다.** `flowRows()` 가 낸 `FlowRow`·`FlowCell` 을 그대로 받아
 *    ⓐ 한 칸을 골라 챔버에 입히고(`speakingCell`) ⓑ 다섯 칸을 **그대로** 카드의 단계 줄에 준다.
 *    격자를 걷어도 계산은 한 곳(`sampleFlowModel.ts`)에 남는다 — 화면이 둘로 갈리지 않는다.
 *
 * ## 무엇이 서버 값이고 무엇이 아닌가
 *
 * | 축 | 출처 | 실물인가 |
 * | --- | --- | --- |
 * | 묶음 구성(환자 수 · 챔버 이름 · 대조) | 레시피 `tray_path.sample_groups[]` | 🟢 실측(`P1`~`P4` + `REF`) |
 * | 단계 진행 · 챔버 상태 | 위 격자 계산의 그 시료 · 지금 단계 | 🟢 (판정만 임시 · BR-49) |
 * | **어느 시료가 어느 묶음인가** | **없다** — 잇는 축이 서버에 0건 | 🔴 임시(`temporarySampleGroups.ts`) |
 *
 * 🔴 **마지막 칸이 이 모델의 설계를 정한다.** 배정은 «주입»받는다(`GroupAssignment`) —
 *    안 넘기면 묶음 구성만 서고 챔버 아래에 **「배정 모름」**이 적힌다(`vacancy`). 그리고 그때도
 *    시료는 **제 카드로 선다**(챔버 자리에 「챔버 배정 없음」) — 잇는 줄만 없을 뿐 시료 목록도
 *    단계도 서버가 준 사실이기 때문이다(`docs/backend-requests.csv` BR-31).
 */

/**
 * **그 시료가 «지금» 말하는 한 가지** — 다섯 칸 중 대표 칸(`speakingCell`)을 그대로 옮긴 것.
 * 카드의 상태 줄과 챔버 칩이 **같은 값**을 쓴다(둘이 갈리지 않게).
 */
export interface BundleState {
  /**
   * 단계 칸과 **같은 어휘**(`FlowCellState`)이거나, 배정을 몰라 상태를 말할 수 없을 때
   * `unassigned` 다. 🔴 `unassigned` 를 `pending` 으로 접지 마라 — 「대기 중」과
   * 「누구 것인지 모른다」는 다른 사실이다.
   */
  state: FlowCellState | 'unassigned'
  /** 지금 말하고 있는 단계. `unassigned` 면 `undefined` */
  stage?: SampleStage
  /** 같은 판정 표. 안 오면 화면은 「지남」까지만 말한다 */
  verdict?: StageVerdict
  errorCode?: string
}

/** 챔버 도형 한 개 — 웰 이름이 붙은 `BundleState` 다 */
export interface ChamberDot extends BundleState {
  /** 🔴 **레시피가 적은 웰 이름 그대로**(`A1`·`C3`). 「챔버 1」 같은 번호를 지어내지 않는다 */
  well: string
  /**
   * **그 챔버만의 진행**(eric 2026-09-28 「챔버별.. 스텝 별로.. 다르게」 · `chamberProgress.ts`).
   * 🔴 `basis: 'well'` 일 때만 챔버마다 갈린다(염색 분주 기록). 모르면 키가 없다.
   */
  progress?: ChamberProgress
}

export interface SampleBundle {
  /** React 키 · 시험이 잡는 식별자. 묶음의 차례에서 나온다(서버 ID 가 아니다) */
  key: string
  /**
   * 🔴 **묶음의 역할.** 대조는 카드 머리에 배지 하나로 구별한다.
   * ⚠ **차례 번호(`patientIndex`)와 묶음 표지(`sampleRef`)는 걷었다**(2026-09-23 · eric
   *   「시료1, P4 이런 게 많은데 이거 빼고 … 텍스트를 최대한 다 빼라고」). 화면이 안 그리는
   *   값을 모델에 남겨 두면 다음 사람이 「응답에 있는 값」으로 착각한다 — 되살리지 마라.
   */
  role: 'patient' | 'reference'
  /** 🔴 **임시 배정**이 준 시료. 없으면 `vacancy` 가 그 까닭을 말한다 */
  sampleId?: string
  /**
   * 한 묶음에 든 시료가 둘 이상일 때 그 전부(레시피 웰 차례 · 첫째가 `sampleId`). 서버가 «시료 1건 = 챔버 1개»만 받는 동안
   * 시험 작업의 한 환자가 시료 2건으로 서는 자리다(BR-121 · 2026-10-14). 하나뿐이면 없다.
   */
  sampleIds?: readonly string[]
  /**
   * 🔴 **묶음 카드에 시료가 없는 까닭** — `sampleId` 가 없는 묶음 카드에만 선다(2026-09-26).
   *    ⓐ `vacant` — 배정 규칙은 돌았는데 **이 묶음에 든 시료가 없다**(묶음이 시료보다 많다) ⇒ 「미배정」
   *    ⓑ `unknown` — 배정 규칙 자체가 없다(`assign` 안 넘김) ⇒ **「모른다」**. 「없다」로 접지 마라
   *    화면은 이 낱말을 카드 머리가 아니라 **챔버 아래 한 마디**에 적는다(eric 2026-09-26).
   * ⚠ 자리 카드(`placeholder`)와 묶음에 못 든 시료(`chambers: []`)에는 없다.
   */
  vacancy?: 'vacant' | 'unknown'
  /**
   * 🔴 **빈 배열은 「챔버 배정 없음」**이다(묶음에 못 든 시료). 종전에 격자 아래
   *    「챔버 배정 없음  FE-… · FE-…」 한 줄로 빼던 것을 **카드로** 들였다 — 그 시료도
   *    단계는 서버가 말해 준다. 줄로 빼면 그 사실이 화면에서 사라진다.
   */
  chambers: ChamberDot[]
  /**
   * 🔴 **카드가 말하는 한 가지.** 챔버 칩도 이 값을 입는다 — 한 시료의 챔버 둘이 서로 다른
   *    상태로 그려지는 축은 서버에도 우리에게도 없다.
   */
  state: BundleState
  /**
   * **단계 진행 5칸** — 종전 격자의 그 줄 **그대로**다(`FlowRow.cells`).
   * 🔴 배정을 모르면 **빈 배열**이다. 카드가 단계를 지어내지 않는다.
   */
  stages: readonly FlowCell[]
  /** 시료 처분. `discarded` 면 카드에 표지가 붙는다. 배정을 모르면 `undefined` */
  status?: SampleStatus
  /**
   * **단계별 시각** — `GET /reports/{sample_id}` 의 `events[]` 에서 온다(`stageTimes.ts`).
   *
   * 🔴 **없는 단계는 키가 없다.** 화면은 그 자리에 `—` 를 적는다 — `0` 이나 「방금」이 아니다.
   * 🔴 **빈 객체가 정상이다.** 보고서를 못 봤을 때(viewer 403 · 아직 안 옴)도, 이번 회차에
   *    사건이 아직 없을 때도 빈 객체다 — 못 본 사유는 카드가 아니라 **구역**이 적는다.
   */
  times: StageTimes
  /**
   * 🔴 **자리만 서는 카드**(도는 작업이 없을 때). `placeholderBundles()` 가 만든다 — 시료도 챔버 이름도
   *    없고 점선 실루엣만 있다. 서버가 준 것이 아니므로 세지도(`sampleCount`) 열지도(`onOpen`) 않는다.
   */
  placeholder?: true
}

/**
 * 도는 작업이 없을 때 **기본 자리** — eric 2026-09-22 「배치가 진행하지 않아도 디폴트 4명, 레퍼런스 하나를
 * 계속 보이게」. 「응답에 없는 값은 자리를 만들지 않는다」의 명시적 예외다(비전 탭과 같은 결) —
 * 장비의 구성(환자 넷 + 대조 하나 · 시험 회차의 꼴)을 화면 뼈대로 늘 보이게 한다.
 * 🔴 **값은 하나도 지어내지 않는다.** 시료 ID · 웰 이름 · 단계 · 시각이 전부 비어 있고 챔버는 이름 없는
 *    점선 실루엣(환자 둘 · 대조 하나 — 현행 레시피의 꼴)이다. 작업이 서면 이 카드는 사라지고 진짜가 선다.
 */
export const PLACEHOLDER_PATIENTS = 4
export const PLACEHOLDER_CHAMBERS: Readonly<Record<SampleBundle['role'], number>> = {
  patient: 2,
  reference: 1,
}

export function placeholderBundles(): SampleBundle[] {
  const blank = (key: string, role: SampleBundle['role']): SampleBundle => ({
    key,
    role,
    chambers: [],
    state: { state: 'unassigned' },
    stages: [],
    times: {},
    placeholder: true,
  })
  return [
    ...Array.from({ length: PLACEHOLDER_PATIENTS }, (_, i) =>
      blank(`ph-p${String(i + 1)}`, 'patient'),
    ),
    blank('ph-ref', 'reference'),
  ]
}

export interface PatientChambers {
  /**
   * 🔴 **모든 시료가 여기 있다.** 레시피 묶음이 먼저 서고, 묶음에 못 든 시료가 뒤에 선다 —
   *    조용히 버리지 않는다(실물에서 반대 경우가 먼저 나왔다: 묶음 5 · 시료 1건).
   */
  bundles: SampleBundle[]
}

/**
 * 배정을 대는 자리. 🔴 **모델이 만들지 않고 받는다**(`StageVerdictLookup` 과 같은 규율).
 * 묶음 차례대로 시료 ID 를 돌려준다 — 모르면 그 자리가 `undefined` 다.
 *
 * ⚠ **남는 시료를 여기서 세지 않는다.** 모델이 「묶음에 안 들어간 줄」로 직접 셈해서
 *   ⓐ 목을 껐을 때도 시료가 화면에서 사라지지 않고 ⓑ 목이 시료를 흘려도 카드가 남는다.
 */
export type GroupAssignment = (
  groups: readonly SampleGroupShape[],
  rows: readonly FlowRow[],
) => readonly (string | undefined)[]

/**
 * **그림이 말하는 한 칸** — 격자의 다섯 칸 중 «지금» 그 시료를 대표하는 것.
 *
 * 규칙은 세 줄이고 순서가 곧 우선순위다.
 *   1. **실패가 있으면 그 칸** — 가장 먼저 걸린 것. 실패는 뒤 단계보다 중요한 사실이다
 *   2. **도는 칸이 있으면 그 칸** — 「지금」이 이긴다
 *   3. 없으면 **표지가 찍힌 마지막 칸**. 그것도 없으면 첫 칸(= 아직 아무 말도 없다)
 *
 * ⚠ 2번의 `active` 는 한 줄에서 **최대 한 칸**이다(`STAGE_DEVICE` 가 단계↔장비 1:1).
 *   그래서 이 함수는 흔들리지 않는다.
 */
export function speakingCell(row: FlowRow): FlowCell {
  const failed = row.cells.find((cell) => cell.verdict === 'fail')
  if (failed !== undefined) return failed

  const active = row.cells.find((cell) => cell.state === 'active')
  if (active !== undefined) return active

  const marked = row.cells.filter((cell) => cell.marked)
  /* `FLOW_STAGES` 가 비지 않으므로 `cells` 도 비지 않는다 — 첫 칸이 언제나 있다 */
  return marked.at(-1) ?? row.cells[0]!
}

/** 줄 하나가 말하는 대표 상태. 🔴 줄이 없으면 「모른다」다 — 「대기」로 접지 않는다 */
function stateOf(row: FlowRow | undefined): BundleState {
  if (row === undefined) return { state: 'unassigned' }
  const cell = speakingCell(row)
  return {
    /* 🔴 폐기는 줄 전체의 사실이다 — 그 칸이 「지남」이어도 챔버는 멈춘 것으로 그린다 */
    state: row.status === 'discarded' ? 'halted' : cell.state,
    stage: cell.stage,
    ...(cell.verdict === undefined ? {} : { verdict: cell.verdict }),
    ...(cell.errorCode === undefined ? {} : { errorCode: cell.errorCode }),
  }
}

export interface PatientChambersInput {
  /** 레시피 `tray_path.sample_groups[]` — `sampleGroupFacts(payload).groups` 를 그대로 넣는다 */
  groups: readonly SampleGroupShape[]
  /** 격자 계산이 낸 **바로 그 줄들**. 여기서 다시 계산하지 않는다 */
  rows: readonly FlowRow[]
  /** 🔴 안 넘기면 배정 열이 통째로 없다(= 지금의 서버 상태) */
  assign?: GroupAssignment | undefined
  /**
   * **묶음에 드는 시료 전부**(`groupedSampleIds`) — 대표가 아닌 같은 묶음의 시료(한 환자의 다른 챔버)는 제 카드를
   * 따로 세우지 않는다. 🔴 안 넘기면 종전대로 대표가 아닌 시료는 전부 「챔버 배정 없음」 카드다.
   */
  grouped?: ReadonlySet<string> | undefined
  /**
   * 웰 → 그 챔버의 진행(`chamberProgress`). 🔴 **안 넘기면 챔버마다 갈리지 않는다**(종전 그대로).
   * 모델이 만들지 않고 받는다 — 배치 자리 · 분주 기록은 부르는 쪽(`useSampleFlow`)이 쥔다.
   */
  progressOf?: ((well: string) => ChamberProgress | undefined) | undefined
  /**
   * **시료 줄이 없을 때(배정만 안다) 카드가 입을 상태.** viewer 는 `GET /samples` 가 403 이라 줄이 없지만
   * 작업의 배정(`Batch.sample_assignments`)은 받는다(eric 2026-09-28 「일단 권한 신경쓰지 말고 보이게」).
   * 🔴 부르는 쪽이 **배치 단계 줄**에서 만든다(시료 단계를 지어내지 않는다). 안 넘기면 종전대로 「모른다」다.
   */
  assignedFallback?: BundleState | undefined
  /**
   * **웰 → 그 챔버의 시료**(서버 배정 — `Batch.sample_assignments` · 없으면 `Sample.assigned_well`).
   * 🔴 **넘기면 카드 = 시료(`sample_id`) 하나다**(eric 2026-10-11 「TEST-20261011-210349-02 이거 하나하나 시료 아니야?」 →
   *    「시료 하나에 챔버가 여러 개면 지금이 맞는데.. 챔버 하나에 시료 하나면 하나씩 되어야지..」). 같은 ID 가 여러 웰이면 한 카드에
   *    그 챔버들, 웰마다 ID 가 다르면 카드 하나씩이다. 레시피 묶음(`sample_groups[]`)은 카드 구성에 쓰지 않는다 — 웰 «차례»와
   *    대조 웰을 아는 데만 쓴다. 안 넘기면 종전(묶음 카드 · 대표 시료 `assign`)이다.
   */
  wellOwners?: ReadonlyMap<string, string> | undefined
  /**
   * 시료별 단계 시각(`stageTimeLookup()`). 🔴 **안 넘기면 시각이 한 칸도 안 선다** —
   * 카드는 `—` 를 적는다. 여기서 시각을 «만들지» 않는다.
   */
  times?: StageTimeLookup | undefined
}

/**
 * 카드 한 벌.
 *
 * 🔴 **묶음이 0이어도 시료는 선다.** 레시피가 묶음을 안 적었으면 「누가 몇 챔버를 쓰는가」를
 *    아는 사람이 없다는 뜻이지 **시료가 없다는 뜻이 아니다** — 그때도 시료마다 카드가 서고
 *    챔버 자리에만 「챔버 배정 없음」이 적힌다. 묶음을 시료 수로 지어내지는 않는다.
 */
export function patientChambers(input: PatientChambersInput): PatientChambers {
  const { groups, rows, assign, grouped, times, progressOf, assignedFallback } = input
  if (input.wellOwners !== undefined) return sampleCards(input, input.wellOwners)

  const sorted = sortFlowRows(rows)
  const byId = new Map(sorted.map((row) => [row.sampleId, row]))
  const assigned = assign?.(groups, sorted) ?? []

  const bundles = groups.map((group, index): SampleBundle => {
    const sampleId = assigned[index]
    const row = sampleId === undefined ? undefined : byId.get(sampleId)
    const state =
      row === undefined && sampleId !== undefined && assignedFallback !== undefined
        ? assignedFallback
        : stateOf(row)

    return {
      key: `g${String(index)}`,
      role: group.role,
      ...(sampleId === undefined
        ? { vacancy: assign === undefined ? ('unknown' as const) : ('vacant' as const) }
        : { sampleId }),
      chambers: group.wells.map((well) => {
        /* 🔴 누구 것인지 모르는 챔버에는 진행을 안 얹는다 — 그 챔버가 이 배치를 돈다는, 모르는 사실이 된다 */
        const progress = sampleId === undefined ? undefined : progressOf?.(well)
        return { well, ...state, ...(progress === undefined ? {} : { progress }) }
      }),
      state,
      stages: row?.cells ?? [],
      ...(row === undefined ? {} : { status: row.status }),
      /* 🔴 **시료를 모르면 시각도 없다.** 묶음 자리만 있고 누가 들었는지 모르는 카드다 */
      times: (sampleId === undefined ? undefined : times?.get(sampleId)) ?? {},
    }
  })

  /*
   * 🔴 **묶음에 못 든 시료도 카드다**(eric 지시 2026-09-21 — 「잃으면 안 되는 것」).
   *    종전에는 그림 아래 한 줄에 ID 만 늘어놓아서, 그 시료가 **어느 단계에 있는지**를 화면이
   *    말하지 않았다. 챔버만 모르는 것이지 단계는 서버가 말해 준다.
   * ⚠ **모델이 직접 센다** — 목이 무엇을 내든 「묶음에 안 들어간 줄」은 여기서 결정된다.
   */
  const taken = new Set(assigned.filter((id): id is string => id !== undefined))
  const loose = sorted
    /* 같은 묶음의 다른 챔버 시료는 그 묶음 카드에 든다(묶음에 시료가 하나라도 있으면 대표가 서 있다 — `serverSampleGroups`) */
    .filter((row) => !taken.has(row.sampleId) && grouped?.has(row.sampleId) !== true)
    .map((row, index): SampleBundle => ({
      key: `u${String(index)}`,
      role: row.reference ? 'reference' : 'patient',
      sampleId: row.sampleId,
      /* 🔴 빈 배열이 곧 「챔버 배정 없음」이다 — 없는 웰 이름을 지어내지 않는다 */
      chambers: [],
      state: stateOf(row),
      stages: row.cells,
      status: row.status,
      times: times?.get(row.sampleId) ?? {},
    }))

  return { bundles: [...bundles, ...loose] }
}

/**
 * **카드 = 레시피 시료 묶음 하나**(웰별 배정이 있을 때 · eric 2026-10-14 「하나의 시료별로 2개의 챔버를 차지하는데.. 이건 9개야..
 * 이 부분 수정해줘. 가장 최근 작업한 표준 레서피 기준으로」). 표준 레시피는 환자 시료 하나가 챔버 둘(P1 = A1 · B1 …), 대조가 하나(C3)다.
 *
 * - 🔴 **카드 구성은 레시피 묶음(`sample_groups[]`)이다** — 묶음 하나에 카드 하나, 그 묶음의 챔버가 다 한 카드에 선다(레시피 차례 그대로).
 *   ⚠ 종전(2026-10-11)은 «카드 = 시료 번호 하나»였다. 서버가 아직 «시료 1건 = 챔버 1개»만 받아(BR-121 · `DUPLICATE_ASSIGNMENT`)
 *   시험 작업이 같은 환자 번호의 시료 2건으로 서므로 카드가 9장으로 갈라졌다. 묶음으로 세면 서버가 시료 1건 = 챔버 2개를 받게 된
 *   뒤에도 같은 모양이다.
 * - 묶음에 든 시료가 둘 이상이면 카드의 시료는 레시피 웰 차례의 첫 시료(`sampleId` · 누르면 그 시료의 서랍)이고, 나머지는
 *   `sampleIds` 에 차례로 싣는다(카드 머리가 함께 적는다). 🔴 서버 응답 차례에 기대지 않는다.
 * - 🔴 **챔버마다 그 칸을 맡은 시료의 상태**를 입는다 — 한 묶음의 두 시료가 서로 다르게 멈출 수 있다(폐기 · 실패).
 * - **대조**는 그 묶음이 레시피 대조 묶음(`role: 'reference'`)이거나 시료 줄이 대조라고 할 때다(카드 머리 「대조」 · 맨 뒤).
 * - 아무 시료도 안 든 레시피 묶음은 종전처럼 «빈 묶음» 카드(`vacant`)다 — 레시피가 그 챔버를 쓰는데 비었다는 사실이다.
 * - 레시피 묶음 밖 웰에 든 시료는 시료마다 카드 하나(웰 이름 차례) · 웰에 안 든 시료는 「챔버 배정 없음」 카드다 — 조용히 버리지 않는다.
 */
function sampleCards(
  input: PatientChambersInput,
  wellOwners: ReadonlyMap<string, string>,
): PatientChambers {
  const { groups, rows, times, progressOf, assignedFallback } = input
  const sorted = sortFlowRows(rows)
  const byId = new Map(sorted.map((row) => [row.sampleId, row]))
  const stateFor = (sampleId: string | undefined): BundleState => {
    const row = sampleId === undefined ? undefined : byId.get(sampleId)
    return row === undefined && assignedFallback !== undefined ? assignedFallback : stateOf(row)
  }
  const dot = (well: string): ChamberDot => {
    const owner = wellOwners.get(well)
    const progress = progressOf?.(well)
    return {
      well,
      ...(owner === undefined ? { state: 'unassigned' as const } : stateFor(owner)),
      ...(progress === undefined ? {} : { progress }),
    }
  }
  /* 카드 하나 — 시료들(레시피 웰 차례) · 챔버들 · 역할 */
  const card = (
    key: string,
    ids: readonly string[],
    wells: readonly string[],
    reference: boolean,
  ): SampleBundle => {
    const lead = ids[0] as string
    const row = byId.get(lead)
    return {
      key,
      role:
        reference || ids.some((id) => byId.get(id)?.reference === true) ? 'reference' : 'patient',
      sampleId: lead,
      ...(ids.length > 1 ? { sampleIds: [...ids] } : {}),
      chambers: wells.map(dot),
      state: stateFor(lead),
      stages: row?.cells ?? [],
      ...(row === undefined ? {} : { status: row.status }),
      times: times?.get(lead) ?? {},
    }
  }

  const placed = new Set<string>()
  const cards: SampleBundle[] = []
  groups.forEach((group, index) => {
    const ids = [
      ...new Set(
        group.wells
          .map((well) => wellOwners.get(well))
          .filter((id): id is string => id !== undefined),
      ),
    ]
    if (ids.length === 0) return
    for (const id of ids) placed.add(id)
    cards.push(card(`g${String(index)}`, ids, group.wells, group.role === 'reference'))
  })

  /* 레시피 묶음 밖 웰에 든 시료 — 시료마다 카드 하나(웰 이름 차례) */
  const recipeWells = new Set(groups.flatMap((group) => group.wells))
  const extraOf = new Map<string, string[]>()
  for (const well of [...wellOwners.keys()]
    .filter((one) => !recipeWells.has(one))
    .sort((a, b) => a.localeCompare(b))) {
    const id = wellOwners.get(well) as string
    if (placed.has(id)) continue
    extraOf.set(id, [...(extraOf.get(id) ?? []), well])
  }
  ;[...extraOf].forEach(([id, wells], index) => {
    placed.add(id)
    cards.push(card(`x${String(index)}`, [id], wells, false))
  })

  /* 아무 시료도 안 든 레시피 묶음 — 종전 «빈 묶음» 카드(챔버 아래 「미배정」) */
  const vacant = groups
    .filter((group) => group.wells.every((well) => !wellOwners.has(well)))
    .map((group, index): SampleBundle => ({
      key: `v${String(index)}`,
      role: group.role,
      vacancy: 'vacant',
      chambers: group.wells.map((well) => ({ well, state: 'unassigned' as const })),
      state: { state: 'unassigned' },
      stages: [],
      times: {},
    }))

  /* 웰에 안 든 시료 — 「챔버 배정 없음」 카드(빈 배열이 곧 그 말이다) */
  const loose = sorted
    .filter((row) => !placed.has(row.sampleId))
    .map((row, index): SampleBundle => ({
      key: `u${String(index)}`,
      role: row.reference ? 'reference' : 'patient',
      sampleId: row.sampleId,
      chambers: [],
      state: stateOf(row),
      stages: row.cells,
      status: row.status,
      times: times?.get(row.sampleId) ?? {},
    }))

  return { bundles: [...cards, ...vacant, ...loose] }
}
